import { useRef } from "react"
import { Button, Center, Drawer, Loader, Text, Group, CloseButton } from "@mantine/core"
import { Trans, useTranslation } from "react-i18next"
import { modals } from "@mantine/modals"
import { useRole, useDeleteRole } from "../hooks/useRoles"
import { useUser } from "../../user/hooks/useUser"
import RoleDetailContent from "./RoleDetailContent"
import RoleFormContent from "./RoleFormContent"

export type DrawerMode = "view" | "create" | "edit"

interface Props {
  opened: boolean
  onClose: () => void
  mode: DrawerMode
  selectedRoleName: string | null
  onDeleted: () => void
  onSwitchToEdit: () => void
}

export default function RoleDrawer({
  opened,
  onClose,
  mode,
  selectedRoleName,
  onDeleted,
  onSwitchToEdit,
}: Props) {
  const { t } = useTranslation()
  const { data: role, isLoading } = useRole(mode !== "create" ? selectedRoleName : null)
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageRoles ?? false
  const deleteMutation = useDeleteRole()
  const formRef = useRef<{ submit: () => void }>(null)
  const isFormMode = mode === "edit" || mode === "create"

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t("adminRoles.confirmDelete"),
      children: <Text size="sm">{t("adminRoles.confirmDeleteMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteMutation.mutate(selectedRoleName!, {
          onSuccess: () => {
            onDeleted()
          },
        })
      },
    })
  }

  const title =
    mode === "create"
      ? t("adminRoles.createTitle")
      : mode === "edit"
        ? t("adminRoles.editTitle")
        : (selectedRoleName ?? "")

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      withCloseButton={false}
    >
      <Drawer.Header>
        <Drawer.Title>
          <Text fw={600} size="md">{title}</Text>
        </Drawer.Title>
        <Group gap="xs">
          {mode === "view" && canManage && (
            <>
              <Button size="xs" variant="default" onClick={onSwitchToEdit} leftSection={<i className="fa fa-pencil" />}>
                <Trans i18nKey="common.edit" />
              </Button>
              <Button size="xs" color="red" onClick={handleDelete} leftSection={<i className="fa fa-trash" />}>
                <Trans i18nKey="common.delete" />
              </Button>
            </>
          )}
          {isFormMode && (
            <Button size="xs" onClick={() => formRef.current?.submit()}>
              <Trans i18nKey={mode === "edit" ? "common.update" : "common.create"} />
            </Button>
          )}
          <CloseButton onClick={onClose} />
        </Group>
      </Drawer.Header>

      {mode === "create" ? (
        <RoleFormContent ref={formRef} onSuccess={onClose} />
      ) : isLoading ? (
        <Center h="100%">
          <Loader />
        </Center>
      ) : !role ? (
        <Text>
          <Trans i18nKey="common.notFound" />
        </Text>
      ) : mode === "edit" ? (
        <RoleFormContent ref={formRef} role={role} onSuccess={onClose} />
      ) : (
        <RoleDetailContent role={role} />
      )}
    </Drawer>
  )
}
